<template>
  <svg
    :width="taille"
    :height="taille"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="epaisseur"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
    class="shrink-0"
  >
    <path v-for="(d, i) in trace" :key="i" :d="d" />
    <circle v-for="(c, i) in cercles" :key="`c${i}`" :cx="c[0]" :cy="c[1]" :r="c[2]" />
    <!-- Tracés PLEINS : quelques dessins se lisent au remplissage et non au
         contour (le demi-disque d'« en cours »). Ils prennent la couleur du
         texte comme le reste. -->
    <path v-for="(d, i) in pleins" :key="`p${i}`" :d="d" fill="currentColor" stroke="none" />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';

/**
 * PICTOGRAMMES VECTORIELS — en remplacement des emoji.
 *
 * Les emoji étaient un choix de facilité qui se voit : « 🗑 » ne se rendait pas
 * du tout sur le poste de l'utilisateur (un bâtonnet à la place), « ✎ » et
 * « ＋⌄ » changent de dessin, de chasse et de couleur selon le système, et
 * aucun ne prend la couleur du texte — donc aucun ne réagit au survol, au mode
 * sombre ni au contraste élevé.
 *
 * Un trait de 1,5 px à extrémités arrondies, en `currentColor`, sur une grille
 * de 24 : le vocabulaire graphique commun des interfaces soignées. Le composant
 * est volontairement MINUSCULE — un dictionnaire de tracés, pas une
 * bibliothèque : embarquer un paquet d'icônes entier pour douze dessins pèserait
 * plus que tout le reste de la page.
 */
const props = withDefaults(
  defineProps<{
    /** Nom du tracé. */
    nom: keyof typeof TRACES;
    /** Côté du carré, en pixels. */
    taille?: number | string;
    epaisseur?: number | string;
  }>(),
  { taille: 16, epaisseur: 1.75 },
);

/**
 * Chaque entrée : les tracés `path`, et éventuellement des cercles — quelques
 * dessins (l'horloge, la personne) sont bien plus courts en cercle qu'en
 * courbes de Bézier.
 */
const TRACES = {
  crayon: { d: ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z'] },
  plus: { d: ['M12 5v14', 'M5 12h14'] },
  corbeille: {
    d: ['M3 6h18', 'M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2', 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6', 'M10 11v6', 'M14 11v6'],
  },
  membres: {
    d: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M22 21v-2a4 4 0 0 0-3-3.87'],
    c: [[9, 7, 4]] as Array<[number, number, number]>,
  },
  alerte: { d: ['M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z', 'M12 9v4', 'M12 17h.01'] },
  coche: { d: ['M20 6 9 17l-5-5'] },
  cadenas: { d: ['M7 11V7a5 5 0 0 1 10 0v4', 'M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z'] },
  horloge: { d: ['M12 7v5l3 2'], c: [[12, 12, 9]] as Array<[number, number, number]> },
  /** Une branche qui part vers la droite : « décomposer en sous-tâches ». */
  branche: { d: ['M6 3v12a3 3 0 0 0 3 3h9', 'M15 15l3 3-3 3'] },
  calendrier: { d: ['M8 2v4', 'M16 2v4', 'M3 6h18v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z', 'M3 10h18'] },
  personne: { d: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'], c: [[12, 7, 4]] as Array<[number, number, number]> },
  document: { d: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z', 'M14 2v6h6'] },
  /* Les trois états d'une tâche, lisibles à la FORME autant qu'à la couleur. */
  cercle: { d: [], c: [[12, 12, 8]] as Array<[number, number, number]> },
  /* Un cercle à MOITIÉ REMPLI : « en route ». Le contour dit le tout, le
     demi-disque plein dit la part faite — la même grammaire que la barre. */
  demiCercle: {
    d: [],
    c: [[12, 12, 8]] as Array<[number, number, number]>,
    p: ['M12 4a8 8 0 0 1 0 16Z'],
  },
  /* --- Centre de notifications --- */
  /** Une bulle de dialogue : « il y a une conversation ici ». */
  bulle: {
    d: ['M21 11.5a8.4 8.4 0 0 1-8.9 8.5 9.5 9.5 0 0 1-2.7-.4L3.5 21l1.5-4.1A8.3 8.3 0 0 1 3.4 11 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z'],
  },
  enveloppe: { d: ['M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z', 'm21 7-9 6-9-6'] },
  /** Un carton d'archives : « classer », le geste qui vide la corbeille d'entrée. */
  boite: {
    d: [
      'M3 3h18a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z',
      'M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8',
      'M10 12h4',
    ],
  },
  /** Chevron : l'ouverture d'un volet. On le fait pivoter en CSS pour le fermer. */
  chevron: { d: ['m6 9 6 6 6-6'] },
  envoyer: { d: ['M22 2 11 13', 'M22 2l-7 20-4-9-9-4Z'] },
  /* --- Navigation latérale (une entrée = un dessin) --- */
  maison: { d: ['m3 10.5 9-7.5 9 7.5', 'M5.5 9.4V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.4', 'M10 21v-6h4v6'] },
  cloche: { d: ['M18 8a6 6 0 0 0-12 0c0 6.5-2.5 8.5-2.5 8.5h17S18 14.5 18 8Z', 'M13.7 20a2 2 0 0 1-3.4 0'] },
  dossier: { d: ['M3 7a1 1 0 0 1 1-1h4.6a1 1 0 0 1 .8.4L11 8.5h9a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z'] },
  /** Une cible : l'action est le lot de travail qu'on VISE. */
  /* Jalon (0075) : le fanion qu'on plante à un point de passage. */
  drapeau: { d: ['M5 21V4', 'M5 4h12l-2.5 4 2.5 4H5'] },
  cible: { d: [], c: [[12, 12, 9], [12, 12, 5], [12, 12, 1.5]] as Array<[number, number, number]> },
  /** Une liste cochée : les tâches. */
  liste: {
    d: ['M9 6h12', 'M9 12h12', 'M9 18h12', 'm3 5.9 1.2 1.2L6.6 4.7', 'm3 11.9 1.2 1.2 2.4-2.4', 'm3 17.9 1.2 1.2 2.4-2.4'],
  },
  /** Des barres : la charge, comparée d'une personne à l'autre. */
  jauge: { d: ['M3 21h18', 'M6 21V12', 'M11 21V4', 'M16 21v-6', 'M20.5 21v-9'] },
  /** Une flèche qui sort du bac : mettre EN PRODUCTION, c'est publier. */
  publier: { d: ['M12 3v12', 'm8 6.8 4-3.8 4 3.8', 'M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4'] },
  /** L'éprouvette du testeur. */
  eprouvette: { d: ['M9 3h6', 'M10 3v6.4L4.7 17.9A2 2 0 0 0 6.4 21h11.2a2 2 0 0 0 1.7-3.1L14 9.4V3'] },
  /** Une planchette : le plan d'actions. */
  planchette: {
    d: [
      'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z',
      'M8 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2',
      'M9 12h6',
      'M9 16h6',
    ],
  },
  ampoule: {
    d: ['M9.5 18h5', 'M10.5 21h3', 'M12 3a6 6 0 0 0-3.6 10.8c.5.4.9 1 .9 1.7V16h5.4v-.5c0-.7.4-1.3.9-1.7A6 6 0 0 0 12 3Z'],
  },
  /** La mallette : le portefeuille de projets présenté au COPIL. */
  mallette: {
    d: ['M3 8h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z', 'M9 8V5.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V8', 'M2 13h20'] },
  /** Un registre : le journal d'audit, ce qui s'écrit ligne à ligne. */
  registre: { d: ['M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z', 'M4 17.5h15', 'M8 8h7', 'M8 12h7'] },
  /**
   * Des curseurs de réglage plutôt qu'un engrenage : à 16 px, les dents d'une
   * roue se referment en tache. Trois glissières se lisent, et disent la même
   * chose — « ici on règle ».
   */
  reglages: {
    d: ['M4 6h16', 'M4 12h16', 'M4 18h16'],
    c: [[9, 6, 2], [15, 12, 2], [7, 18, 2]] as Array<[number, number, number]>,
  },
  livre: {
    d: [
      'M12 7.2V21',
      'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3Z',
    ],
  },
  boussole: { d: ['m15.5 8.5-2.2 5.3-5.3 2.2 2.2-5.3Z'], c: [[12, 12, 9]] as Array<[number, number, number]> },
  // Développement : les deux chevrons du code, et la barre oblique entre eux.
  code: { d: ['m8 17-5-5 5-5', 'm16 7 5 5-5 5', 'M14 4 10 20'] },
  // Un conteneur : la caisse et son couvercle, pour les fichiers Docker.
  conteneur: {
    d: ['M3 8.5 12 4l9 4.5v7L12 20l-9-4.5Z', 'm3 8.5 9 4.5 9-4.5', 'M12 13v7'],
  },
  // Une prise : les ports d'écoute des VM.
  prise: { d: ['M9 3v6', 'M15 3v6', 'M6 9h12v3a6 6 0 0 1-12 0Z', 'M12 18v3'] },
  copie: { d: ['M9 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1Z', 'M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1'] },
  // Pièces jointes : le trombone dit qu'il y a un document, l'œil qu'on peut
  // le voir sans l'enregistrer, la flèche qu'on l'enregistre.
  trombone: { d: ['m21.4 11.6-8.5 8.5a5.5 5.5 0 0 1-7.8-7.8l8.5-8.5a3.7 3.7 0 0 1 5.2 5.2l-8.5 8.5a1.8 1.8 0 0 1-2.6-2.6l7.8-7.8'] },
  oeil: { d: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z'], c: [[12, 12, 3]] as Array<[number, number, number]> },
  telecharger: { d: ['M12 3v12', 'm7 10 5 5 5-5', 'M5 21h14'] },
  // Étiquette personnelle (0111) : la forme du badge qu'on pose sur un projet.
  etiquette: {
    d: ['M12.6 2.6A2 2 0 0 0 11.2 2H4a2 2 0 0 0-2 2v7.2a2 2 0 0 0 .6 1.4l8.7 8.7a2.4 2.4 0 0 0 3.4 0l6.6-6.6a2.4 2.4 0 0 0 0-3.4Z'],
    c: [[7.5, 7.5, 1.2]] as Array<[number, number, number]>,
  },
  cadenasOuvert: { d: ['M7 11V7a5 5 0 0 1 9.9-1', 'M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z'] },
} as const;

const trace = computed(() => TRACES[props.nom].d);
const cercles = computed(
  () => ('c' in TRACES[props.nom] ? (TRACES[props.nom] as { c: Array<[number, number, number]> }).c : []),
);
const pleins = computed(
  () => ('p' in TRACES[props.nom] ? (TRACES[props.nom] as { p: readonly string[] }).p : []),
);
</script>
